import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  fetchRecords,
  fetchRecord,
  createRecord,
  softDeleteRecord,
  restoreRecord,
  approveRecord,
  declineRecord,
  finalSubmitRecord,
  checkFinalSubmit,
  transitionToUnderReview,
  fetchDeclineComments,
  fetchFieldComments,
  fetchFormComments,
  fetchRecordComments,
  resolveComment,
  updateComment,
  deleteComment,
  addReplyComment,
  addFormComment,
  addRecordComment,
  type RecordsFilter,
} from './records-api'

export function useRecordsQuery(
  projectId: string | undefined,
  filter: RecordsFilter = {},
  page = 0,
  pageSize = 25
) {
  return useQuery({
    queryKey: ['records', projectId, filter, page, pageSize],
    queryFn: () => fetchRecords(projectId!, filter, page, pageSize),
    enabled: !!projectId,
  })
}

export function useRecordQuery(recordId: string | undefined) {
  return useQuery({
    queryKey: ['record', recordId],
    queryFn: () => fetchRecord(recordId!),
    enabled: !!recordId,
  })
}

export function useCreateRecordMutation(projectId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (userId: string) => createRecord(projectId, userId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['records', projectId] }),
  })
}

export function useSoftDeleteRecordMutation(projectId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ recordId, userId }: { recordId: string; userId: string }) =>
      softDeleteRecord(recordId, userId),
    onSuccess: (_data, { recordId }) => {
      qc.invalidateQueries({ queryKey: ['records', projectId] })
      qc.invalidateQueries({ queryKey: ['record', recordId] })
    },
  })
}

export function useRestoreRecordMutation(projectId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ recordId, userId }: { recordId: string; userId: string }) =>
      restoreRecord(recordId, userId),
    onSuccess: (_data, { recordId }) => {
      qc.invalidateQueries({ queryKey: ['records', projectId] })
      qc.invalidateQueries({ queryKey: ['record', recordId] })
    },
  })
}

export function useApproveRecordMutation(projectId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      recordId,
      userId,
      userRole,
      comment,
    }: {
      recordId: string
      userId: string
      userRole: string
      comment?: string
    }) => approveRecord(recordId, userId, userRole, projectId, comment),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['records', projectId] })
      qc.invalidateQueries({ queryKey: ['record', vars.recordId] })
    },
  })
}

export function useDeclineRecordMutation(projectId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      recordId,
      userId,
      userRole,
      comment,
    }: {
      recordId: string
      userId: string
      userRole: string
      comment: string
    }) => declineRecord(recordId, userId, userRole, projectId, comment),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['records', projectId] })
      qc.invalidateQueries({ queryKey: ['record', vars.recordId] })
      qc.invalidateQueries({ queryKey: ['record-comments', vars.recordId] })
      qc.invalidateQueries({ queryKey: ['decline-comments', vars.recordId] })
    },
  })
}

/**
 * Uses the dry-run RPC `final_submit_record_check`. Returns the richer
 * {@link FinalSubmitCheck} shape: missingForms + openDeclineComments +
 * orphanDrafts (informational, does not block).
 */
export function useCheckFinalSubmitMutation(projectId: string) {
  return useMutation({
    mutationFn: ({ recordId }: { recordId: string }) =>
      checkFinalSubmit(recordId, projectId),
  })
}

/**
 * Continuously-cached version of the same RPC. Used to drive the record
 * progress header + the "enabled?" state of the final-submit button
 * without firing a fresh request on every click.
 */
export function useFinalSubmitCheckQuery(
  recordId: string | undefined,
  projectId: string | undefined,
) {
  return useQuery({
    queryKey: ['final-submit-check', recordId],
    queryFn: () => checkFinalSubmit(recordId!, projectId!),
    enabled: !!recordId && !!projectId,
    staleTime: 5_000,
  })
}

export function useFinalSubmitMutation(projectId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      recordId,
      userId,
      userRole,
    }: {
      recordId: string
      userId: string
      userRole: string
    }) => finalSubmitRecord(recordId, projectId, userId, userRole),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['records', projectId] })
      qc.invalidateQueries({ queryKey: ['record', vars.recordId] })
    },
  })
}

export function useTransitionToUnderReviewMutation(projectId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      recordId,
      userId,
      userRole,
    }: {
      recordId: string
      userId: string
      userRole: string
    }) => transitionToUnderReview(recordId, projectId, userId, userRole),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['record', vars.recordId] })
    },
  })
}

export function useDeclineCommentsQuery(recordId: string | undefined) {
  return useQuery({
    queryKey: ['decline-comments', recordId],
    queryFn: () => fetchDeclineComments(recordId!),
    enabled: !!recordId,
  })
}

export function useResolveCommentMutation(recordId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ commentId, userId }: { commentId: string; userId: string }) =>
      resolveComment(commentId, userId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['decline-comments', recordId] })
      qc.invalidateQueries({ queryKey: ['record-comments', recordId] })
      qc.invalidateQueries({ queryKey: ['field-comments', recordId] })
    },
  })
}

export function useUpdateCommentMutation(recordId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ commentId, body }: { commentId: string; body: string }) =>
      updateComment(commentId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['decline-comments', recordId] })
      qc.invalidateQueries({ queryKey: ['record-comments', recordId] })
      qc.invalidateQueries({ queryKey: ['field-comments', recordId] })
      qc.invalidateQueries({ queryKey: ['form-comments', recordId] })
    },
  })
}

export function useDeleteCommentMutation(recordId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ commentId, userId }: { commentId: string; userId: string }) =>
      deleteComment(commentId, userId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['decline-comments', recordId] })
      qc.invalidateQueries({ queryKey: ['record-comments', recordId] })
      qc.invalidateQueries({ queryKey: ['field-comments', recordId] })
      qc.invalidateQueries({ queryKey: ['form-comments', recordId] })
    },
  })
}

export function useAddReplyMutation(recordId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (params: Parameters<typeof addReplyComment>[0]) => addReplyComment(params),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['record-comments', recordId] })
      qc.invalidateQueries({ queryKey: ['field-comments', recordId] })
      qc.invalidateQueries({ queryKey: ['form-comments', recordId] })
    },
  })
}

export function useFieldCommentsQuery(recordId: string | undefined, submissionId: string | undefined) {
  return useQuery({
    queryKey: ['field-comments', recordId, submissionId],
    queryFn: () => fetchFieldComments(recordId!, submissionId!),
    enabled: !!recordId && !!submissionId,
  })
}

export function useFormCommentsQuery(recordId: string | undefined, submissionId: string | undefined) {
  return useQuery({
    queryKey: ['form-comments', recordId, submissionId],
    queryFn: () => fetchFormComments(recordId!, submissionId!),
    enabled: !!recordId && !!submissionId,
  })
}

export function useAddFormCommentMutation(recordId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (params: Omit<Parameters<typeof addFormComment>[0], never>) =>
      addFormComment(params),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['form-comments', recordId, vars.submissionId] })
    },
  })
}

export function useRecordCommentsQuery(recordId: string | undefined) {
  return useQuery({
    queryKey: ['record-comments', recordId],
    queryFn: () => fetchRecordComments(recordId!),
    enabled: !!recordId,
  })
}

export function useAddRecordCommentMutation(recordId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (params: Parameters<typeof addRecordComment>[0]) => addRecordComment(params),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['record-comments', recordId] })
    },
  })
}
